import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { 
  MessageSquare, 
  AlertTriangle, 
  Bell, 
  Calendar,
  Filter,
  Download,
  Trash2,
  Search,
  SlidersHorizontal
} from 'lucide-react';
import { useStore } from '../../store/useStore';
import { format } from 'date-fns';
import { Message, MessageType, MessagePriority } from '../../types';
import toast from 'react-hot-toast';

export const CommunicationLogs: React.FC = () => {
  const messages = useStore((state) => state.messages);
  const clearLogs = useStore((state) => state.clearCommunicationLogs);
  const archiveMessage = useStore((state) => state.archiveMessage);

  const [filters, setFilters] = useState({
    type: '' as MessageType | '',
    priority: '' as MessagePriority | '',
    department: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  const [showFilters, setShowFilters] = useState(false);

  const getMessageIcon = (type: string) => {
    switch (type) {
      case 'ALERT':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'REMINDER':
        return <Bell className="w-4 h-4 text-blue-500" />;
      case 'BULLETIN':
        return <Calendar className="w-4 h-4 text-emerald-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  const filteredMessages = messages.filter(message => {
    if (filters.type && message.type !== filters.type) return false;
    if (filters.priority && message.priority !== filters.priority) return false;
    if (filters.department && !message.departments.includes(filters.department)) return false;
    if (filters.startDate && new Date(message.createdAt) < new Date(filters.startDate)) return false;
    if (filters.endDate && new Date(message.createdAt) > new Date(filters.endDate)) return false;
    if (filters.search && !message.title.toLowerCase().includes(filters.search.toLowerCase()) &&
        !message.content.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  const handleExport = () => {
    const exportData = filteredMessages.map(msg => ({
      type: msg.type,
      priority: msg.priority,
      title: msg.title,
      content: msg.content,
      sender: msg.sender.name,
      departments: msg.departments.join(', '),
      createdAt: format(new Date(msg.createdAt), 'MMM d, yyyy h:mm a'),
      acknowledgments: msg.acknowledgments.length
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `communication-logs-${format(new Date(), 'yyyy-MM-dd')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Logs exported successfully');
  };

  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all communication logs?')) {
      clearLogs();
      toast.success('Communication logs cleared');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <MessageSquare className="w-5 h-5 text-purple-600" />
          </div>
          <h2 className="text-lg font-semibold">Communication Logs</h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={<SlidersHorizontal className="w-4 h-4" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<Download className="w-4 h-4" />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            className="text-red-600 hover:text-red-700"
            onClick={handleClearLogs}
          >
            Clear
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value as MessageType }))}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All Types</option>
                <option value="ALERT">Alert</option>
                <option value="REMINDER">Reminder</option>
                <option value="BULLETIN">Bulletin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value as MessagePriority }))}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="">All Departments</option>
                <option value="SALES">Sales</option>
                <option value="PURCHASE">Purchase</option>
                <option value="ACCOUNTS_RECEIVABLE">Accounts Receivable</option>
                <option value="ACCOUNTS_PAYABLE">Accounts Payable</option>
                <option value="OPERATIONS">Operations</option>
                <option value="WAREHOUSE">Warehouse</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border rounded-md"
            />
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filteredMessages.map((message) => (
          <div
            key={message.id}
            className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-1">{getMessageIcon(message.type)}</div>
                <div>
                  <h3 className="font-medium text-gray-900">{message.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{message.content}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <span>From: {message.sender.name}</span>
                    <span>•</span>
                    <span>To: {message.departments.join(', ')}</span>
                    <span>•</span>
                    <span>{format(new Date(message.createdAt), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  message.priority === 'URGENT' ? 'bg-red-100 text-red-700' :
                  message.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                  message.priority === 'MEDIUM' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {message.priority}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 className="w-4 h-4" />}
                  onClick={() => {
                    archiveMessage(message.id);
                    toast.success('Message archived');
                  }}
                  className="text-gray-400 hover:text-gray-500"
                />
              </div>
            </div>
          </div>
        ))}

        {filteredMessages.length === 0 && (
          <p className="text-center text-gray-500 py-8">No messages found</p>
        )}
      </div>
    </Card>
  );
};